/**
 * CyVine 7-Step Growth Path
 * 
 * The servant-minded sales methodology for nurturing Shoots (Opportunities)
 * into healthy Vines (Customers) through honest discovery and Phase 1 Branches.
 */

export const GROWTH_PATH_STEPS = [
  {
    step: 1,
    id: 'preparation',
    cyvineName: 'Root & Soil Check',
    title: 'Preparation',
    description: 'Summarize what we already know about the customer and what we need to learn.',
    icon: '🌱',
    color: 'amber',
    purpose: 'Understand the ground before we plant',
    keyActivities: [
      'Research company background and industry',
      'Review contact\'s professional history',
      'Identify what we hope to learn',
      'Prepare thoughtful discovery questions',
      'Draft a simple, clear agenda'
    ],
    questions: [
      'What does this company do and who do they serve?',
      'What might their current pain points be based on their industry?',
      'Who are the key stakeholders we might meet?',
      'What specific workflows might we explore?',
      'What would a healthy Phase 1 look like for them?'
    ],
    outputs: [
      'Company research summary',
      'Contact background notes',
      'Discovery question list',
      'Meeting agenda draft'
    ],
    timeEstimate: '30-60 min',
    servantMindset: 'We\'re preparing to understand them, not to pitch them.'
  },
  {
    step: 2,
    id: 'introduction',
    cyvineName: 'First Touch & Trust',
    title: 'Introduction & Rapport',
    description: 'Propose a simple, human intro and agenda that sets a collaborative tone.',
    icon: '🤝',
    color: 'green',
    purpose: 'Build genuine connection before business',
    keyActivities: [
      'Acknowledge something genuine about their work',
      'Share brief relevant experience',
      'Set collaborative, curious tone',
      'Confirm agenda and time',
      'Get them talking about their world'
    ],
    questions: [
      'What drew you to this company/role?',
      'How has your year been going so far?',
      'What\'s keeping you busy these days?',
      'What does a typical week look like for you?'
    ],
    outputs: [
      'Rapport notes',
      'Common ground identified',
      'Tone established',
      'Agenda confirmed'
    ],
    timeEstimate: '5-10 min',
    servantMindset: 'We\'re here to understand their world, not impress them with ours.'
  },
  {
    step: 3,
    id: 'discovery',
    cyvineName: 'Knot-Finding in the Vine',
    title: 'Discovery',
    description: 'List the customer\'s manual work hotspots, current tools, constraints, and impact.',
    icon: '🔍',
    color: 'blue',
    purpose: 'Find the knots that slow their growth',
    keyActivities: [
      'Ask about current workflows and processes',
      'Identify manual work and repetitive tasks',
      'Understand current tools and systems',
      'Quantify time and cost impacts',
      'Explore constraints and blockers'
    ],
    questions: [
      'Walk me through how you currently handle [process]...',
      'Where do things get stuck or slow down?',
      'What takes more time than it should?',
      'How many people touch this process?',
      'What happens when something goes wrong?',
      'What have you tried before?',
      'What would "better" look like for you?'
    ],
    outputs: [
      'Pain points documented',
      'Current workflows mapped',
      'Impact quantified (time, cost, frustration)',
      'Constraints identified',
      'Stakeholders mapped'
    ],
    timeEstimate: '20-30 min',
    servantMindset: 'We\'re listening to find real problems, not hunting for a sale.'
  },
  {
    step: 4,
    id: 'summary',
    cyvineName: 'Mirror Back',
    title: 'Summary Feedback',
    description: 'Draft a short "Here\'s what I heard..." summary to confirm with the customer.',
    icon: '🪞',
    color: 'purple',
    purpose: 'Confirm we truly understand before we suggest',
    keyActivities: [
      'Restate problems in their words',
      'Summarize the impact as they described it',
      'Confirm our understanding is accurate',
      'Ask what we might have missed',
      'Validate priorities'
    ],
    mirrorTemplate: `"Here's what I heard...

You're spending about [X hours/week] on [process] because [reason].
The main pain points are:
1. [Pain point 1]
2. [Pain point 2]
3. [Pain point 3]

This is causing [impact on team/business].
Did I capture that correctly? What did I miss?"`,
    outputs: [
      'Summary statement',
      'Client confirmation',
      'Any corrections noted',
      'Priorities validated'
    ],
    timeEstimate: '5-10 min',
    servantMindset: 'We\'re checking our understanding, not rushing to our solution.'
  },
  {
    step: 5,
    id: 'phase1',
    cyvineName: 'Phase 1 Branch Plan',
    title: 'Proposed Solution',
    description: 'Define a small, honest Phase 1 Branch (scope, not-in-scope, success metrics).',
    icon: '🌿',
    color: 'emerald',
    purpose: 'Propose a small, honest first step',
    keyActivities: [
      'Explain how we could address their specific problems',
      'Define clear Phase 1 scope',
      'Be explicit about what\'s NOT in Phase 1',
      'Set measurable success criteria',
      'Discuss timeline and investment'
    ],
    phase1Template: {
      problem: 'The core problem we\'re solving',
      scope: ['What IS included in Phase 1'],
      notInScope: ['What we\'ll save for later'],
      successMetrics: ['How we\'ll know it worked'],
      timeline: 'Estimated duration',
      investment: 'Ballpark range'
    },
    outputs: [
      'Phase 1 scope defined',
      'Exclusions clear',
      'Success metrics set',
      'Timeline discussed',
      'Investment range shared'
    ],
    timeEstimate: '10-15 min',
    servantMindset: 'We\'re proposing what genuinely helps, not what maximizes our revenue.'
  },
  {
    step: 6,
    id: 'closing',
    cyvineName: 'Planting the Branch',
    title: 'Decision & Next Steps',
    description: 'Suggest respectful questions to move toward a clear yes/no/when.',
    icon: '✋',
    color: 'orange',
    purpose: 'Understand their path to a decision',
    keyActivities: [
      'Understand their decision process',
      'Identify all stakeholders involved',
      'Discuss timeline for decision',
      'Address remaining concerns',
      'Agree on clear next steps'
    ],
    questions: [
      'Does this approach make sense for what you\'re trying to solve?',
      'Who else would need to weigh in on this?',
      'What does your decision process typically look like?',
      'Is there a timeline we should be aware of?',
      'What would you need from us to move forward?',
      'What concerns do you still have?'
    ],
    outputs: [
      'Decision process understood',
      'Stakeholders identified',
      'Timeline established',
      'Next steps agreed',
      'Concerns addressed'
    ],
    timeEstimate: '5-10 min',
    servantMindset: 'We\'re seeking clarity, not pressuring for a close.'
  },
  {
    step: 7,
    id: 'followup',
    cyvineName: 'Ongoing Tending',
    title: 'Follow-Up',
    description: 'Recommend appropriate follow-up actions based on outcome.',
    icon: '🌳',
    color: 'teal',
    purpose: 'Nurture the relationship regardless of outcome',
    keyActivities: [
      'Send recap within 24 hours',
      'Complete any promised follow-ups',
      'If won: Hand off smoothly to delivery',
      'If lost: Capture reason and stay in touch',
      'If pending: Maintain appropriate contact'
    ],
    outcomes: {
      won: {
        title: 'Handoff to Branch (Project)',
        actions: [
          'Introduce delivery team',
          'Share all discovery notes',
          'Schedule kickoff meeting',
          'Ensure smooth transition'
        ]
      },
      lost: {
        title: 'Capture & Stay Connected',
        actions: [
          'Document loss reason (be honest)',
          'Thank them for their time',
          'Offer to stay in touch',
          'Schedule future check-in if appropriate'
        ]
      },
      pending: {
        title: 'Maintain Momentum',
        actions: [
          'Send promised materials',
          'Schedule follow-up touchpoint',
          'Provide value (article, insight, intro)',
          'Respect their timeline'
        ]
      }
    },
    outputs: [
      'Recap sent',
      'Follow-ups completed',
      'Handoff initiated (if won)',
      'Loss reason captured (if lost)',
      'Next touchpoint scheduled'
    ],
    timeEstimate: 'Ongoing',
    servantMindset: 'Every interaction is about their success, whether they buy or not.'
  }
] as const;

export type GrowthPathStepId = typeof GROWTH_PATH_STEPS[number]['id'];
export type GrowthPathStep = typeof GROWTH_PATH_STEPS[number];

export const SHOOT_STATUSES = [
  { value: 'new', label: 'New Seed', color: 'gray', description: 'Just arrived, needs qualification' },
  { value: 'discovery_scheduled', label: 'Discovery Scheduled', color: 'blue', description: 'First call booked' },
  { value: 'in_discovery', label: 'In Discovery', color: 'purple', description: 'Active discovery conversations' },
  { value: 'proposal_pending', label: 'Phase 1 Planning', color: 'amber', description: 'Building Phase 1 proposal' },
  { value: 'proposal_sent', label: 'Proposal Sent', color: 'orange', description: 'Waiting for decision' },
  { value: 'negotiating', label: 'Negotiating', color: 'pink', description: 'Working through details' },
  { value: 'won', label: 'Won → Branch', color: 'green', description: 'Converting to project' },
  { value: 'lost', label: 'Lost', color: 'red', description: 'Did not proceed' },
  { value: 'paused', label: 'Paused', color: 'slate', description: 'On hold for now' },
] as const;

export const LOSS_REASONS = [
  { value: 'timing', label: 'Not the right time', description: 'They have other priorities now' },
  { value: 'budget', label: 'Budget constraints', description: 'Cost didn\'t fit their current situation' },
  { value: 'fit', label: 'Not a good fit', description: 'Our solution wasn\'t right for their needs' },
  { value: 'competitor', label: 'Chose another vendor', description: 'Went with someone else' },
  { value: 'internal', label: 'Doing it internally', description: 'Decided to build/do it themselves' },
  { value: 'no_decision', label: 'No decision made', description: 'Process stalled, no clear outcome' },
  { value: 'stakeholder', label: 'Stakeholder change', description: 'Key person left or changed' },
  { value: 'other', label: 'Other', description: 'Different reason' },
] as const;

export const URGENCY_LEVELS = [
  { value: 'low', label: 'Low', color: 'slate', description: 'No specific timeline' },
  { value: 'medium', label: 'Medium', color: 'blue', description: 'Within a few months' },
  { value: 'high', label: 'High', color: 'amber', description: 'Within weeks' },
  { value: 'critical', label: 'Critical', color: 'red', description: 'Urgent need' },
] as const;

export const PAIN_CATEGORIES = [
  { value: 'manual_work', label: 'Manual Work', icon: '⚙️', description: 'Repetitive tasks that could be automated' },
  { value: 'data_silos', label: 'Data Silos', icon: '🏝️', description: 'Information trapped in different systems' },
  { value: 'communication', label: 'Communication', icon: '💬', description: 'Bottlenecks in information flow' },
  { value: 'tracking', label: 'Tracking', icon: '📊', description: 'Difficulty tracking status or progress' },
  { value: 'reporting', label: 'Reporting', icon: '📈', description: 'Time-consuming report generation' },
  { value: 'integration', label: 'Integration', icon: '🔗', description: 'Systems that don\'t talk to each other' },
  { value: 'process_gaps', label: 'Process Gaps', icon: '🕳️', description: 'Missing or broken processes' },
  { value: 'other', label: 'Other', icon: '❓', description: 'Other type of pain point' },
] as const;

export function getStepById(id: string) {
  return GROWTH_PATH_STEPS.find(step => step.id === id);
}

export function getStepByNumber(stepNumber: number) {
  return GROWTH_PATH_STEPS.find(step => step.step === stepNumber);
}

export function getNextStep(currentStepId: string) {
  const currentIndex = GROWTH_PATH_STEPS.findIndex(step => step.id === currentStepId);
  if (currentIndex >= 0 && currentIndex < GROWTH_PATH_STEPS.length - 1) {
    return GROWTH_PATH_STEPS[currentIndex + 1];
  }
  return null;
}

export function getPreviousStep(currentStepId: string) {
  const currentIndex = GROWTH_PATH_STEPS.findIndex(step => step.id === currentStepId);
  if (currentIndex > 0) {
    return GROWTH_PATH_STEPS[currentIndex - 1];
  }
  return null;
}